import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubble";
import { UserRepository } from "@amityco/ts-sdk";
import { AmityUiKitChat } from "@amityco/ui-kit-open-source";

import { User } from "../../entities/User";
import { RootState } from "../../state";
import defaultAvatarImg from "../../img/default-user-avatar.png";
import defaultCoverImage from "../../img/default_profile_banner.webp";
import UserName from "../UserName/UserName";
import CommunityService from "../../services/communityService";
import DialogService from "../Dialog/dialogService";
import NotificationService from "../Notification/notificationService";

import settings from "../../config/settings";

import { COLLECTION_TAB } from "../../pages/MyProfile/MyProfile";
import ShareBar from "../ShareBar/ShareBar";
import { COLLECTION_SECTION } from "../../pages/MyProfile/Collection/Collection";
import { SETTINGS_TAB } from "../../pages/MyProfile/Settings";
import AmityProvider from "../Bootstrap/AmityProvider";

interface UserHeaderProps {
  user: User;
  setTab: Function;
  totalShirts: number | undefined;
  selectedTab: COLLECTION_TAB;
}

const UserHeader = ({ user, totalShirts, selectedTab }: UserHeaderProps) => {
  const [followStatus, setFollowStatus] = useState<
    Amity.FollowStatus[] | undefined
  >(undefined);
  const loginData = useSelector((state: RootState) => state.login);
  const defaultProfileImage = defaultAvatarImg;
  const [follows, setFollows] = useState(false);
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [prettyUrl, setPrettyUrl] = useState<string | undefined>(undefined);

  const { success } = NotificationService();

  const copyToClipBoard = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      success('"' + prettyUrl + '" copied to clipboard');
    }
  };

  useEffect(() => {
    setUrl(
      settings.PUBLIC_URL +
        "/my-profile/" +
        user.userId +
        "/" +
        COLLECTION_TAB.COLLECTION +
        "/" +
        COLLECTION_SECTION.OVERVIEW
    );
    setPrettyUrl(
      "collecticos.com/my-profile/" +
        user.userId +
        "/" +
        COLLECTION_TAB.COLLECTION +
        "/" +
        COLLECTION_SECTION.OVERVIEW
    );
  }, [user.userId]);

  const isMobile = useMediaQuery("(max-width: 426px)");

  const navigate = useNavigate();

  const { createChannel, follow, unfollow } = CommunityService();
  const { open } = DialogService();

  const getNewPath = (newTab: COLLECTION_TAB) => {
    let currentPath = "/my-profile/" + user.userId + "/" + newTab;
    if (newTab === COLLECTION_TAB.COLLECTION) {
      currentPath += "/" + COLLECTION_SECTION.OVERVIEW;
    } else {
      if (newTab === COLLECTION_TAB.SETTINGS) {
        currentPath += "/" + SETTINGS_TAB.MY_ACCOUNT;
      }
    }
    return currentPath;
  };

  const setTab = (tabTarget: COLLECTION_TAB) => {
    const targetPath = getNewPath(tabTarget);
    navigate(targetPath);
  };

  const sendNewMessage = () => {
    createChannel(
      String(user.userId),
      user.userName + " and " + loginData.userName
    )
      .then(function (data) {
        open(
          "Send New Message",
          <AmityProvider>
            <AmityUiKitChat defaultChannelId={data.channelId} />
          </AmityProvider>,
          []
        );
      })
      .catch(function (errorData) {
        console.log(errorData);
      });
  };

  const handleFollow = () => {
    follow(String(user.userId))
      .then(() => {
        setFollows(true);
      })
      .catch(function (error) {
        alert(error.message);
      });
  };

  const handleUnfollow = () => {
    unfollow(String(user.userId))
      .then(() => {
        setFollows(false);
      })
      .catch(function (error) {
        alert(error.message);
      });
  };

  useEffect(() => {
    if (loginData?.userId) {
      UserRepository.Relationship.getFollowings(
        { userId: String(loginData.userId) },
        ({ data: followers, onNextPage, hasNextPage, loading, error }) => {
          if (error) {
            // Handle any errors that occur during retrieving data
          }
          if (loading) {
            // Handle the loading state, e.g., show a loading spinner
          }
          if (followers) {
            // Process the data
            setFollowStatus(followers);
          }
        }
      );
    }
  }, [loginData]);

  useEffect(() => {
    if (followStatus && user.userId) {
      const followValue = followStatus.some(
        (follow) => follow.to === String(user.userId)
      );
      setFollows(followValue);
    }
  }, [followStatus]);

  return (
    <Box
      className="shadow-box"
      sx={{
        position: "relative",
        width: "100%",
        height: {
          xs: loginData?.userId !== user.userId ? "400px" : "350px",
          sm: "320px",
        },
      }}
      aria-label="Profile-Relative-Box"
    >
      <Box
        aria-label="Profile-Banner"
        sx={{ width: "100%", height: "calc(100% - 60px)" }}
      >
        <img
          src={
            user.headerImage?.url ? user.headerImage?.url : defaultCoverImage
          }
          alt="cover-pic"
          style={{
            borderRadius: "10px",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: { xs: "1.5rem", sm: "70px" },
          left: { xs: "50%", sm: "35px" },
          bottom: { xs: "unset", sm: "20px" },
          transform: { xs: "translateX(-50%)", sm: "translateX(0%)" },
          width: { xs: "150px", sm: "190px" },
          display: "inline-block",
          backgroundColor: "#fff",
          padding: "4px",
          borderRadius: { xs: "50%", sm: "10px" },
          fontSize: "0px !important",
          boxShadow: "0px 0px 17px rgba(0, 0, 0, 0.2) !important",
        }}
        aria-label="Profile-Avatar-Box"
      >
        <img
          src={
            user.profileImage?.url
              ? user.profileImage?.url
              : defaultProfileImage
          }
          alt="DP"
          style={{
            width: isMobile ? "150px" : "100%",
            height: isMobile ? "150px" : "100%",
            objectFit: "cover",
            margin: "0 auto",
            borderRadius: isMobile ? "50%" : "0.5rem",
          }}
        />
      </Box>

      <Box
        sx={{
          height: { xs: "fit-content", sm: "100px" },
          position: "absolute",
          right: {
            xs: "0",
            sm: "0px",
            md: "80px",
            lg: loginData?.userId !== user.userId ? "120px" : "450px",
          },
          bottom: "60px",
          left: { xs: "0px", sm: "225px" },
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: { sm: "center", md: "start" },
          paddingY: "15px",
          borderRadius: { xs: "0px 0px 10px 10px", sm: "0px" },
        }}
        aria-label="BlurBox"
        rowGap={{ xs: 2, md: 0 }}
      >
        <Box
          sx={{
            padding: "0px 1rem",
            width: {
              xs: "100%",
              sm: loginData?.userId !== user.userId ? "45%" : "55%",
            },
            textAlign: { xs: "center", sm: "center", md: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "center", md: "left" },
            }}
          >
            <UserName user={user} variant={"userName"} />
          </Box>

          <Typography
            variant={"userName"}
            sx={{
              fontSize: "0.9rem !important",
              fontWeight: "800 !important",
              textAlign: { xs: "center", sm: "center", md: "left" },
            }}
          >
            {totalShirts ?? 0} Shirts
          </Typography>
        </Box>
        <Divider
          orientation="vertical"
          sx={{
            bgcolor: "white",
            display: { md: "block", xs: "none" },
          }}
        />
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: loginData?.userId !== user.userId ? "25%" : "40%",
            },
            height: "100%",
            display: { xs: "none", sm: "flex" },
            justifyContent: "center",
          }}
        >
          {user?.userId && <ShareBar targetUserId={user.userId} />}
        </Box>
        {loginData?.userId !== user.userId && (
          <>
            <Divider
              orientation="vertical"
              sx={{
                bgcolor: "white",
                display: { md: "block", xs: "none" },
              }}
            />

            <Box
              sx={{
                width: { xs: "100%", sm: "35%" },
                height: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: {
                  xs: "10px 20px 10px 20px",
                  sm: "10px 20px 10px 20px",
                  md: "10px 16px 0px 16px",
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  backgroundColor: "white",
                  color: "",
                  display: { xs: "flex", sm: "none" },
                  width: "30px",
                  height: "30px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={copyToClipBoard}
              >
                <InsertLinkIcon />
              </IconButton>
              <Button
                startIcon={follows ? <RemoveIcon /> : <AddIcon />}
                variant="user"
                onClick={follows ? handleUnfollow : handleFollow}
                sx={{
                  width: {
                    xs: "fit-content",
                    sm: "100%",
                    md: "100%",
                    lg: "fit-content",
                  },
                  marginBottom: {
                    xs: "0px",
                    sm: "8px",
                    md: "8px",
                    lg: "0px",
                  },
                }}
              >
                {follows ? "Unfollow" : "Follow"}
              </Button>
              <Button
                startIcon={
                  <ChatBubbleOutlineIcon sx={{ fontSize: "17px !important" }} />
                }
                onClick={sendNewMessage}
                variant="user"
                sx={{
                  width: {
                    xs: "fit-content",
                    sm: "100%",
                    md: "100%",
                    lg: "fit-content",
                  },
                }}
              >
                Send message
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: "white !important",
          minHeight: "60px",
          borderRadius: "10px",
          paddingLeft: { xs: "0px", sm: "250px" },
          display: "flex",
          justifyContent: { xs: "center", sm: "start" },
          alignItems: "center",
          flexDirection: "row",
          boxShadow: "0px 0px 17px rgba(0, 0, 0, 0.05) !important",
        }}
        aria-label="Profile_details"
      >
        <Button
          variant={
            selectedTab === COLLECTION_TAB.COLLECTION
              ? "customTextSelected"
              : "customText"
          }
          onClick={() => setTab(COLLECTION_TAB.COLLECTION)}
        >
          Collection
        </Button>
        <Button
          variant={
            selectedTab === COLLECTION_TAB.FOLLOWERS
              ? "customTextSelected"
              : "customText"
          }
          onClick={() => setTab(COLLECTION_TAB.FOLLOWERS)}
        >
          Followers
        </Button>
        <Button
          variant={
            selectedTab === COLLECTION_TAB.FOLLOWING
              ? "customTextSelected"
              : "customText"
          }
          onClick={() => setTab(COLLECTION_TAB.FOLLOWING)}
        >
          Following
        </Button>
        {loginData.userId === user.userId && (
          <Button
            variant={
              selectedTab === COLLECTION_TAB.SETTINGS
                ? "customTextSelected"
                : "customText"
            }
            onClick={() => setTab(COLLECTION_TAB.SETTINGS)}
          >
            Settings
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UserHeader;
